import { toast } from "react-toastify";
import axios from "axios";
//import { useInfiniteQuery } from "react-query";
//import iconSuccess from "./../assets/images/icons8-check-64.png";
//import iconWarning from "./../assets/images/menu-warning.png";
import iconError from "./../assets/images/icons-error-48.png";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import {
    BLOB_ACCOUNT_IMAGE,
    BLOB_BOIMAGE_CONTAINER,
    BLOB_BRAND_IMAGE,
    BLOB_COMPANY_IMAGE,
    BLOB_PRODUCT_CONTAINER,
    BLOB_STAFF_CONTAINER,
    BLOB_STORE_CONTAINER
} from "../config/constant";
import toastError from "./../assets/images/toast-error.png";
import toastInfo from "./../assets/images/toast-info.png";
import toastSuccess from "./../assets/images/toast-success.png";
import toastWarning from "./../assets/images/toast-warning.png";
import { axiosInstance } from "../api/axios";

export function showNotification({
    message = "",
    type = "success", // success, warning, error
    autoClose = 5000,
    setDefaultNotificationCard
}) {
    setDefaultNotificationCard({ show: true, message: message, type: type, autoClose: autoClose });
}

/* #region MAIN FUNCTIONS */

/** Displays a toast message with the specified options.
 *
 * @param {Object} props - The properties of the toast message.
 * @param {string} props.title - The title of the toast message.
 * @param {string} props.description - The description of the toast message.
 * @param {string} props.message - The message of the toast.
 * @param {string} [props.type="success"] - The type of the toast message. Can be "success", "warning", "error", or "info".
 * @param {number} [props.autoClose=5000] - The time in milliseconds before the toast message automatically closes.
 * @param {function} props.onClose - The function to call when the toast message closes.
 */
export function showToastMessage({ title, description, message, type = "success", autoClose = 5000, onClose = undefined }) {
    const descriptionStr = description || message;

    const htmlBody = descriptionStr ? (
        <div>
            <div>{title}</div>
            <div
                style={{ fontSize: "12px", color: "var(--text-icons-gray-100, #D1D1D1)", marginTop: "5px" }}
                dangerouslySetInnerHTML={{ __html: descriptionStr }}
            />
        </div>
    ) : (
        title
    );

    switch (type) {
        case "success":
            toast.success(htmlBody, {
                theme: "dark",
                icon: () => <img alt="success" src={toastSuccess} />,
                progressStyle: { background: "#00BB00" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "warning":
            toast.warning(htmlBody, {
                theme: "dark",
                icon: () => <img alt="warning" src={toastWarning} />,
                progressStyle: { background: "#ff9900" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "error":
            toast.error(htmlBody, {
                theme: "dark",
                icon: () => <img alt="error" src={toastError} />,
                progressStyle: { background: "#FF3636" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "info":
            toast.info(htmlBody, {
                theme: "dark",
                icon: () => <img alt="info" src={toastInfo} />,
                progressStyle: { background: "#00AEEF" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        default:
            console.error("Invalid type provided for showToastMessage method!");
            return;
    }
}

/** Makes an API request to the specified endpoint with the provided data.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.endpoint - The API endpoint to call. If endpoint has a slash then will consider this is a BOCommon endpoint.
 * @param {Object} params.data - The data to send in the request body.
 * @param {string} [params.method="POST"] - The HTTP method to use for the request (supported: "GET", "POST", "PUT", "DELETE").
 * @param {function} [params.setLoader=null] - A function to show a loader while the request is in progress.
 * @param {boolean} [params.onErrorShowMsg=true] - Whether to show an error message if the request fails. Will show by default.
 * @param {function} params.onError - A function to call if an error occurs during the request.
 * @return {Promise} A promise that resolves with the response data if the request is successful, or rejects with an error if the request fails.
 */
export function makeApiRequest({ endpoint, data, method = "POST", setLoader = null, onErrorShowMsg = true, onError }) {
    return new Promise((resolve, reject) => {
        const tmpData = { platform: "heyBO" };

        let api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;
        let requestData = data ? { ...tmpData, ...data } : { ...tmpData };

        // Check if the endpoint is a BOCommon endpoint (contains a slash)
        const useBOCommon = endpoint.includes("/");

        // If using BOCommon backend instead, we need to use the common endpoint and pass the context
        if (useBOCommon) {
            const BOCommonOperation = endpoint.split("/")[0];
            const BOCommonEndpoint = endpoint.split("/")[1];

            const commonEndpoint = "callCommonAPI";
            api_url = `${process.env.REACT_APP_API_URL}${commonEndpoint}?code=${process.env.REACT_APP_API_CODE}`;
            requestData = {
                context: BOCommonOperation,
                endpoint: BOCommonEndpoint,
                paramsData: { ...data }
            };
        }

        // Start the loader
        if (setLoader) setLoader(true);

        let axiosPromise;

        // Assign axiosPromise based on HTTP method
        switch (method.toUpperCase()) {
            case "GET":
                axiosPromise = axiosInstance.get(api_url, { params: requestData });
                break;
            case "POST":
                axiosPromise = axiosInstance.post(api_url, requestData);
                break;
            case "PUT":
                axiosPromise = axiosInstance.put(api_url, requestData);
                break;
            case "DELETE":
                axiosPromise = axiosInstance.delete(api_url, { data: requestData });
                break;
            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }

        // Now chain the handlers
        axiosPromise
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "An error occurred while trying to process your request.";
                    if (onErrorShowMsg) showToastMessage({ title: message, type: "error" });
                    if (onError) onError(response);
                    resolve(response);
                }
            })
            .catch(error => {
                if (onError) onError(error);
                const message = error?.message ? `${error.message}.` : "An error occurred while trying to process your request.";
                if (onErrorShowMsg) showToastMessage({ title: message, type: "error" });
                //reject(error);
            })
            .finally(() => {
                // Stop the loader
                if (setLoader) {
                    setTimeout(() => {
                        setLoader(false);
                    }, 0);
                }
            });
    });
}

/** Checks if a functionality is only available in localhost. Useful for debugging purposes.
 * @param {string} functionality - The name of the functionality to check.
 * @returns {boolean} Returns true in localhost, false otherwise.
 * If the debugPass is set to "gamechanger", it will return false regardless of the hostname.
 */
export function isLocalhost(functionality, ignoreLog = false) {
    const isLocalhost =
        window.location.hostname === "localhost" ||
        ["192.168.", "10.", "127.0."].some(prefix => window.location.hostname.startsWith(prefix));

    //search if functionality is found in the localStorage with prefix "debugPass_{functionality}" as true
    //example: functionality= "promptAIchat" => localStorage.debugPass_promptAIchat = "true"
    const debugPassActive =
        localStorage.getItem(`debugPass_${functionality}`) === "true" || localStorage.getItem(`debugPass_${functionality}`) === true;
    if (debugPassActive) return false;

    if (localStorage.getItem("debugPass") === "gamechanger") return false;
    if (functionality === "Logout Timer") return true;

    if (functionality && !ignoreLog) console.log(`NOTE: Functionality only available in localhost: ${functionality}`);
    else if (!functionality) console.error("NOTE: Functionality only available in localhost, but no functionality name provided.");
    return isLocalhost;
}
/* #endregion */

/* DEPRECATED. Use showNotification instead with type = "success"
export function showSuccessMessage(message, autoClose = 5000, onClose = () => {}) {
    toast.success(message, {
        theme: "dark",
        icon: ({ theme, type }) => <img alt="success" src={iconSuccess} />,
        autoClose: autoClose,
        onClose: () => {
            if (onClose) onClose();
        }
    });
} */

/* DEPRECATED. Use showNotification instead with type = "warning"
export function showWarningMessage(message, autoClose = 5000, onClose = () => {}) {
    toast.success(message, {
        theme: "dark",
        icon: ({ theme, type }) => <img alt="warning" src={iconWarning} />,
        autoClose: autoClose,
        onClose: () => {
            if (onClose) onClose();
        }
    });
} */

/**
 * @deprecated Use showNotification instead with type = "error"
 */
export function showOldErrorMessage(message, autoClose = 5000, onClose = () => {}) {
    toast.error(message, {
        theme: "dark",
        icon: ({ theme, type }) => <img alt="error" src={iconError} />,
        autoClose: autoClose,
        onClose: () => {
            if (onClose) onClose();
        }
    });
}

export function defaultApiError(error, errorCode = 400) {
    let message = "Something went wrong.";
    //message = error?.message ? `${message} ${error.message}.` : message;
    message = `${message} Code ${errorCode}`;
    console.log(error);

    showOldErrorMessage(message);
}

export function showConfirmationMessage(message, onConfirm = () => {}, onDeny = () => {}) {
    Swal.fire({
        title: "",
        text: "",
        html: message,
        icon: "warning",
        showDenyButton: true,
        allowOutsideClick: false,
        confirmButtonColor: "#205380",
        denyButtonColor: "#205380",
        confirmButtonText: "YES",
        denyButtonText: "NO",
        background: "#1d2530",
        color: "#fff",
        iconColor: "rgb(255, 153, 0)",
        width: "500px",
        padding: "2em",
        reverseButtons: false
    }).then(result => {
        if (result.isConfirmed) {
            onConfirm();
        }
        if (result.isDenied || result.isDismissed) {
            onDeny();
        }
    });
}

export function makePostRequest(
    endpoint,
    data,
    useFormData = false,
    setShowLoader = null,
    onError = undefined,
    onUploadProgress = undefined,
    onErrorShowMsg = true,
    aborter = null
) {
    return new Promise((resolve, reject) => {
        const api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;

        let axiosConfig = !useFormData
            ? {}
            : {
                  "Content-Type": "multipart/form-data",
                  "Access-Control-Allow-Origin": "*"
              };

        const tmpData = { platform: "heyBO" };
        data = data ? { ...tmpData, ...data } : { ...tmpData };

        if (setShowLoader) setShowLoader(true);

        if (onUploadProgress) axiosConfig = { ...axiosConfig, onUploadProgress: onUploadProgress };
        if (aborter?.signal) axiosConfig = { ...axiosConfig, signal: aborter?.signal };

        axios
            .post(api_url, data, axiosConfig)
            .then(response => {
                if (response?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "Something went wrong.";

                    const error = new Error(message);
                    if (onErrorShowMsg) defaultApiError(error);

                    if (onError) onError(error);
                    resolve(response);
                }
            })
            .catch(error => {
                if (onErrorShowMsg) defaultApiError(error);

                if (onError) onError(error);
                //reject(error);
            })
            .finally(() => {
                if (setShowLoader) {
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 0);
                }
            });
    });
}

export function makeCommonPostRequest(
    context,
    endpoint,
    data,
    useFormData = false,
    setShowLoader = null,
    onError = undefined,
    onUploadProgress = undefined,
    onErrorShowMsg = true
) {
    const commonEndpoint = "callCommonAPI";
    const params = {
        context: context,
        endpoint: endpoint,
        paramsData: data,
        platform: "heyBO"
    };

    return new Promise((resolve, reject) => {
        const api_url = `${process.env.REACT_APP_API_URL}${commonEndpoint}?code=${process.env.REACT_APP_API_CODE}`;

        let axiosConfig = !useFormData
            ? {}
            : {
                  "Content-Type": "multipart/form-data",
                  "Access-Control-Allow-Origin": "*"
              };

        if (setShowLoader) setShowLoader(true);

        if (onUploadProgress) axiosConfig = { ...axiosConfig, onUploadProgress: onUploadProgress };

        axios
            .post(api_url, params, axiosConfig)
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "Something went wrong.";

                    const error = new Error(message);
                    if (onErrorShowMsg) {
                        //TODO: Check if we should be using showOldErrorMessage or defaultApiError
                        showOldErrorMessage(message);
                        //defaultApiError(error);
                    }

                    if (onError) onError(error);
                    resolve(response);
                }
            })
            .catch(error => {
                if (onErrorShowMsg) defaultApiError(error);
                if (onErrorShowMsg) defaultApiError(error);

                if (onError) onError(error);
                //reject(error);
            })
            .finally(() => {
                if (setShowLoader) {
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 0);
                }
            });
    });
}

/*
export const usePostInfiniteQuery = (
    queryKey,
    api,
    formData,
    onSuccess,
    useBOCommon,
    BOCommonOperation,
    keepPreviousData = false
) => {
    const queryKeyArray = typeof queryKey === "string" ? [queryKey] : queryKey;
    return useInfiniteQuery({
        queryKey: queryKeyArray,
        queryFn: async ({ pageParam = 1 }) => {
            formData.page = pageParam;

            let tmp = null;
            if (!useBOCommon) tmp = await makePostRequest(api, formData);
            else tmp = await makeCommonPostRequest(BOCommonOperation, api, formData);
            const res = tmp?.data;
            if (res?.status !== 200) {
                if (![undefined, "ok", "success", ""].includes(res?.message?.toLowerCase()))
                    showErrorMessage(res?.message);
                return [];
            }

            return res;
        },

        getNextPageParam: (lastPage, pages) => {
            return lastPage?.data?.nextPage;
        },
        onSuccess: onSuccess,
        keepPreviousData: keepPreviousData
    });
};*/

//TODO: makeGetRequest make return promise
export function makeGetRequest(endpoint, data = [], onError = () => {}) {
    let api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;

    // If data is provided, append it as query parameters
    if (data) {
        const params = new URLSearchParams();

        // Loop through the data object and add each key-value pair to the URL
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                params.append(key, data[key]);
            }
        }

        // Append the parameters to the URL
        api_url += `&${params.toString()}`;
    }

    return axios
        .get(api_url)
        .then(response => {
            if (response.status === 200) {
                return response;
            } else {
                throw new Error("Error code " + response.status);
            }
        })
        .catch(error => {
            defaultApiError(error);

            console.error("API Request Error:", error);
            //throw error;
            onError(error);
        });
}

export function uploadBlobFile(fileBlob, container, setShowLoader = null) {
    return new Promise((resolve, reject) => {
        if (
            ![
                BLOB_ACCOUNT_IMAGE,
                BLOB_BOIMAGE_CONTAINER,
                BLOB_BRAND_IMAGE,
                BLOB_COMPANY_IMAGE,
                BLOB_COMPANY_IMAGE,
                BLOB_PRODUCT_CONTAINER,
                BLOB_STAFF_CONTAINER,
                BLOB_STORE_CONTAINER
            ].includes(container)
        ) {
            reject(`Container ${container} not recognized for uploading files. Try again later.`);
            return;
        }

        if (!fileBlob || !fileBlob.name) {
            reject("File selected is not recognized as compatible for upload. Try again later.");
            return;
        }

        const formData = new FormData();
        formData.append("containerName", container);
        formData.append("files", fileBlob);
        formData.append("blobName", fileBlob.name);

        makePostRequest("uploadBlobFile", formData, true, setShowLoader)
            .then(res => {
                resolve(res);
            })
            .catch(error => reject(error));
    });
}

export async function getUserIP() {
    const res = await axios.get("https://api.ipify.org/?format=json");
    return res.data.ip;
}

export function openNewPage(url, setDefaultNotificationCard) {
    if (!url) {
        showNotification({
            message: "Can't open new page, URL is not defined.",
            type: "error",
            setDefaultNotificationCard
        });
        return;
    }

    window.open(url, "_blank");
}

export function stripHtmlTags(html) {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
}

export function showEnv() {
    console.log("Env Vars:", process.env);
}

export function changeUrlWithoutReloading(newPath) {
    window.history.pushState(null, "", newPath);
}
export const getUserInfo = callback => {
    if (!localStorage.getItem("isLoggedIn")) {
        return;
    }
    makePostRequest("getUser", { _id: localStorage.getItem("pid") }, false)
        .then(res => {
            const { data } = res;
            if (data?.status === 200) {
                callback(data?.data);
            }
        })
        .catch(e => {});
};
